// Thin client for /transfer-api/v1 (tasks/contracts/transfer-v1.md). No retries here; the engines decide.
// Wire-shape assumptions are listed in CONTRACT-QUESTIONS.md (Q3).
import { TransferApiError } from './errors.js';

export const API_BASE = '/transfer-api/v1';
const MAX_JSON_BYTES = 8 * 1024 * 1024;

function retryAfterMs(res) {
  const v = res.headers.get('retry-after');
  if (!v) return null;
  const s = Number(v);
  if (Number.isFinite(s)) return Math.min(120_000, Math.max(0, s * 1000));
  const d = Date.parse(v);
  return Number.isFinite(d) ? Math.min(120_000, Math.max(0, d - Date.now())) : null;
}

async function errorFrom(res) {
  let code = null, message = null;
  try {
    const body = await res.json();
    if (body && body.error) { code = body.error.code ?? null; message = body.error.message ?? null; }
  } catch { /* non-JSON error body */ }
  return new TransferApiError(res.status, code, message, { retryAfterMs: retryAfterMs(res) });
}

async function readJson(res) {
  const len = Number(res.headers.get('content-length'));
  if (Number.isFinite(len) && len > MAX_JSON_BYTES) throw new TransferApiError(res.status, 'response_too_large', 'Response too large');
  return res.json();
}

/** Whole body, refused (and the stream cancelled) as soon as it passes `max` bytes. */
export async function readCapped(res, max) {
  const tooLarge = () => new TransferApiError(res.status, 'response_too_large', 'Response too large');
  const declared = Number(res.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > max) { try { await res.body?.cancel(); } catch { /* closed */ } throw tooLarge(); }
  if (!res.body || typeof res.body.getReader !== 'function') {
    const buf = new Uint8Array(await res.arrayBuffer());
    if (buf.length > max) throw tooLarge();
    return buf;
  }
  const reader = res.body.getReader();
  const parts = [];
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    n += value.length;
    if (n > max) { try { await reader.cancel(); } catch { /* closed */ } throw tooLarge(); }
    parts.push(value);
  }
  const out = new Uint8Array(n);
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}

function adminOverview(r) {
  if (!r || typeof r !== 'object' || !Array.isArray(r.tenants)) throw new TransferApiError(200, 'unexpected_shape', 'The admin API answered in an unexpected shape.');
  return { tenant: r.tenant ?? null, policy: r.policy && typeof r.policy === 'object' ? r.policy : null, tenants: r.tenants.filter((t) => t && typeof t.tenant === 'string' && t.policy && typeof t.policy === 'object') };
}

// enrollment-v1 admin projection: only these fields are ever kept, whatever else a response carries (paths, hosts,
// credentials or new internal columns can never reach the page by accident).
const ENROLLMENT_FIELDS = ['jobId', 'sourceId', 'state', 'attempts', 'reason', 'resourceId', 'nextAttemptAt', 'lastRequestedAt', 'completedAt', 'createdAt', 'updatedAt'];
const SAFE_TEXT = /^[A-Za-z0-9:._-]{1,128}$/;
export function adminEnrollments(r, tenant) {
  const bad = () => new TransferApiError(200, 'unexpected_shape', 'The admin API answered in an unexpected shape.');
  if (!r || typeof r !== 'object' || !Array.isArray(r.jobs) || r.jobs.length > 1000) throw bad();
  const p = r.page && typeof r.page === 'object' ? r.page : null;
  if (p && typeof p.tenant === 'string' && p.tenant !== tenant) throw new TransferApiError(200, 'stale_tenant', 'The admin API answered for a different site.');
  const jobs = r.jobs.map((j) => {
    if (!j || typeof j !== 'object' || typeof j.jobId !== 'string' || typeof j.state !== 'string') throw bad();
    const out = {};
    for (const k of ENROLLMENT_FIELDS) {
      const v = j[k];
      if (v == null) { out[k] = null; continue; }
      if (k === 'attempts') { out[k] = Number.isSafeInteger(v) && v >= 0 ? v : null; continue; }
      if (/At$/.test(k)) { out[k] = typeof v === 'string' && Number.isFinite(Date.parse(v)) ? v : null; continue; }
      out[k] = typeof v === 'string' && SAFE_TEXT.test(v) ? v : null; // ids, state and reason codes only
    }
    if (!out.jobId || !out.state) throw bad();
    return out;
  });
  const hasMore = !!(p && p.hasMore === true);
  const nextCursor = hasMore && typeof p.nextCursor === 'string' && p.nextCursor.length > 0 && p.nextCursor.length <= 256 ? p.nextCursor : null;
  return { jobs, hasMore: hasMore && !!nextCursor, nextCursor };
}

function adminTenantPolicy(r) {
  if (!r || typeof r !== 'object' || typeof r.tenant !== 'string' || !r.policy || typeof r.policy !== 'object') throw new TransferApiError(200, 'unexpected_shape', 'The admin API answered in an unexpected shape.');
  return { tenant: r.tenant, policy: r.policy };
}

/**
 * @param {{ baseUrl?: string, fetch?: typeof fetch }} [o]
 */
export function createApi(o = {}) {
  const base = (o.baseUrl ?? API_BASE).replace(/\/$/, '');
  const f = o.fetch ?? ((...a) => globalThis.fetch(...a));
  const enc = encodeURIComponent;

  async function call(method, path, { token, json, body, headers, signal, raw } = {}) {
    const h = new Headers(headers || {});
    if (token) h.set('authorization', `Bearer ${token}`);
    let payload = body;
    if (json !== undefined) { h.set('content-type', 'application/json'); payload = JSON.stringify(json); }
    if (!raw) h.set('accept', 'application/json');
    const res = await f(base + path, { method, headers: h, body: payload, signal, credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw await errorFrom(res);
    if (raw) return res;
    if (res.status === 204) return null;
    return readJson(res);
  }

  return {
    base,
    catalog: (signal) => call('GET', '/catalog', { signal }).then((r) => (Array.isArray(r) ? r : (r && r.resources) || [])),
    createSession: ({ resourceId, password }, signal) =>
      call('POST', '/sessions', { json: password ? { resourceId, direction: 'download', password } : { resourceId, direction: 'download' }, signal }),
    /** Renewal path: `{ manifest, policy }` (a bare manifest is tolerated). */
    manifest: (s, signal) => call('GET', `/sessions/${enc(s.sessionId)}/manifest`, { token: s.token, signal }).then((r) => (r && r.manifest ? r : { manifest: r, policy: null })),

    /** Standard v1 .torrent bytes for an eligible public resource (bearer session auth). */
    async torrent(s, signal) {
      const res = await call('GET', `/sessions/${enc(s.sessionId)}/torrent`, { token: s.token, signal, raw: true });
      const len = Number(res.headers.get('content-length'));
      if (Number.isFinite(len) && len > 4 * 1024 * 1024) throw new TransferApiError(res.status, 'response_too_large', 'Torrent metadata too large');
      return new Uint8Array(await res.arrayBuffer());
    },

    /** One piece as bytes. */
    async piece(s, index, signal) {
      const res = await call('GET', `/sessions/${enc(s.sessionId)}/pieces/${index}`, { token: s.token, signal, raw: true });
      return new Uint8Array(await res.arrayBuffer());
    },

    /** Bytes [start, endInclusive] through the HTTPS fallback route. Requires a 206 with a matching Content-Range. */
    async range(s, start, endInclusive, signal) {
      const res = await call('GET', `/sessions/${enc(s.sessionId)}/file`, { token: s.token, signal, raw: true, headers: { range: `bytes=${start}-${endInclusive}` } });
      if (res.status !== 206) throw new TransferApiError(res.status, 'range_ignored', 'Server ignored the Range header');
      const cr = /^bytes (\d+)-(\d+)\/(\d+|\*)$/.exec(res.headers.get('content-range') || '');
      if (!cr || Number(cr[1]) !== start || Number(cr[2]) !== endInclusive) throw new TransferApiError(res.status, 'range_mismatch', 'Content-Range does not match the request');
      return new Uint8Array(await res.arrayBuffer());
    },

    event: (s, ev) => call('POST', `/sessions/${enc(s.sessionId)}/events`, { token: s.token, json: ev }),

    // Partial relay (network-relay-http-v1). Scheduling hints and advisory confirmations only: none of these earns
    // useful-download credit, and none returns a native ticket or a whole-file URL.
    relayReport: (s, body, signal) => call('POST', `/sessions/${enc(s.sessionId)}/network/relay/report`, { token: s.token, json: body, signal }),
    relayPlan: (s, signal) => call('POST', `/sessions/${enc(s.sessionId)}/network/relay/plan`, { token: s.token, json: {}, signal }),
    relayLease: (s, leaseId, signal) => call('GET', `/sessions/${enc(s.sessionId)}/network/relay/leases/${enc(leaseId)}`, { token: s.token, signal }),
    relayOrigin: (s, leaseId, signal) => call('POST', `/sessions/${enc(s.sessionId)}/network/relay/leases/${enc(leaseId)}/origin`, { token: s.token, json: {}, signal }),
    relayComplete: (s, leaseId, sha256, signal) => call('POST', `/sessions/${enc(s.sessionId)}/network/relay/leases/${enc(leaseId)}/complete`, { token: s.token, json: { sha256 }, signal }),
    /** Stored .torrent bytes of a leased (other) resource, read under a hard 4 MiB cap whatever the headers claim. */
    async relayLeaseTorrent(s, leaseId, signal) {
      const res = await call('GET', `/sessions/${enc(s.sessionId)}/network/relay/leases/${enc(leaseId)}/torrent`, { token: s.token, signal, raw: true });
      return readCapped(res, 4 * 1024 * 1024);
    },

    /** Upload creation needs the operator credential in the isolated demo (Addendum B); chunk writes use the returned token. */
    createUpload: ({ name, sizeBytes, mime, password, isPublic = false, operatorToken }, signal) =>
      call('POST', '/uploads', { token: operatorToken, json: { name, sizeBytes, mime, ...(password ? { password } : {}), public: !!isPublic }, signal }),
    putPiece: (u, index, bytes, signal) =>
      call('PUT', `/uploads/${enc(u.uploadId)}/pieces/${index}`, { token: u.token, body: bytes, headers: { 'content-type': 'application/octet-stream' }, signal }),
    commitUpload: (u, sha256, signal) => call('POST', `/uploads/${enc(u.uploadId)}/commit`, { token: u.token, json: { sha256 }, signal }),

    /**
     * Admin host only (Addendum B). Policy is per content tenant:
     *  - overview(): `{ tenant:null, policy, tenants:[{tenant,policy}] }`
     *  - policy(t) / updatePolicy(t, delta): `{ tenant, policy }` (PATCH body is the delta itself)
     *  - metrics(): unwraps `{ metrics }`
     */
    admin: (secret) => {
      const q = (tenant) => (tenant ? `?tenant=${enc(tenant)}` : '');
      return {
        overview: (signal) => call('GET', '/admin/policy', { token: secret, signal }).then(adminOverview),
        policy: (tenant, signal) => call('GET', `/admin/policy${q(tenant)}`, { token: secret, signal }).then(adminTenantPolicy),
        updatePolicy: (tenant, patch, signal) => call('PATCH', `/admin/policy${q(tenant)}`, { token: secret, json: patch, signal }).then(adminTenantPolicy),
        resources: (tenant, signal) => call('GET', `/admin/resources${q(tenant)}`, { token: secret, signal }).then((r) => (Array.isArray(r) ? r : (r && r.resources) || [])),
        /** enrollment-v1: `GET /admin/enrollments?tenant=&limit=&cursor=` (opaque cursor, URL-encoded). Read-only. */
        enrollments: (tenant, { cursor = null, limit = 50 } = {}, signal) =>
          call('GET', `/admin/enrollments?tenant=${enc(tenant)}&limit=${enc(String(limit))}${cursor ? `&cursor=${enc(cursor)}` : ''}`, { token: secret, signal }).then((r) => adminEnrollments(r, tenant)),
        metrics: (signal) => call('GET', '/admin/metrics', { token: secret, signal }).then((r) => (r && r.metrics && typeof r.metrics === 'object' ? r.metrics : {})),
        backfill: (tenant, body, signal) => call('POST', `/admin/manifests${q(tenant)}`, { token: secret, json: body, signal }),
      };
    },
  };
}
